// frontend/src/pages/Admin/Instituciones.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../../styles/Admin/Instituciones.css';

const Instituciones = () => {
  const { user } = useAuth();
  const [instituciones, setInstituciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [institucionSeleccionada, setInstitucionSeleccionada] = useState(null);
  const [documentosInstitucion, setDocumentosInstitucion] = useState([]);
  const [usuariosInstitucion, setUsuariosInstitucion] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [filtros, setFiltros] = useState({
    estatus: 'todos',
    busqueda: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [formData, setFormData] = useState({
    nombre: '',
    rfc: '',
    direccion: '',
    telefono: '',
    email: '',
    representanteLegal: '',
    fechaConstitucion: '',
    estatus: 'pendiente',
    documentosRequeridos: []
  });

  const [nuevoDocumentoReq, setNuevoDocumentoReq] = useState('');

  const estatusOptions = [
    { value: 'todos', label: 'Todos los estatus' },
    { value: 'activo', label: 'Activo', color: '#28a745' },
    { value: 'inactivo', label: 'Inactivo', color: '#dc3545' },
    { value: 'pendiente', label: 'Pendiente', color: '#ffc107' }
  ];

  // Cargar instituciones
  const cargarInstituciones = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.estatus !== 'todos') params.append('estatus', filtros.estatus);
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
      if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
      if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);

      const response = await api.get(`/instituciones?${params.toString()}`);
      
      if (response.data && response.data.data) {
        setInstituciones(Array.isArray(response.data.data) ? response.data.data : []);
      } else if (Array.isArray(response.data)) {
        setInstituciones(response.data);
      } else {
        setInstituciones([]);
      }
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
      setInstituciones([]);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarInstituciones();
  }, [cargarInstituciones]);

  // Cargar detalle de institución
  const cargarDetalleInstitucion = async (id) => {
    try {
      const [instRes, docsRes, usersRes] = await Promise.all([
        api.get(`/instituciones/${id}`),
        api.get(`/instituciones/${id}/documentos`),
        api.get(`/instituciones/${id}/usuarios`)
      ]);

      setInstitucionSeleccionada(instRes.data.data);
      setDocumentosInstitucion(docsRes.data.data || []);
      setUsuariosInstitucion(usersRes.data.data || []);
      setActiveTab('info');
      setShowDetalleModal(true);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  };

  // Guardar institución (crear o actualizar)
  const handleGuardarInstitucion = async (e) => {
    e.preventDefault();
    
    try {
      // Validar campos obligatorios
      if (!formData.nombre || !formData.rfc) {
        alert('El nombre y RFC son obligatorios');
        return;
      }

      if (institucionSeleccionada?._id) {
        // Actualizar
        await api.put(`/instituciones/${institucionSeleccionada._id}`, formData);
        alert('✅ Institución actualizada correctamente');
      } else {
        // Crear nueva
        await api.post('/instituciones', formData);
        alert('✅ Institución creada correctamente');
      }

      setShowModal(false);
      resetFormData();
      cargarInstituciones();
    } catch (error) {
      console.error('Error al guardar:', error);
      alert(error.response?.data?.msg || 'Error al guardar la institución');
    }
  };

  // Eliminar institución
  const handleEliminarInstitucion = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta institución? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await api.delete(`/instituciones/${id}`);
      alert('✅ Institución eliminada correctamente');
      setShowDetalleModal(false);
      cargarInstituciones();
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(error.response?.data?.msg || 'Error al eliminar la institución');
    }
  };

  // Abrir modal para editar
  const abrirModalEditar = (institucion) => {
    setInstitucionSeleccionada(institucion);
    setFormData({
      nombre: institucion.nombre,
      rfc: institucion.rfc,
      direccion: institucion.direccion || '',
      telefono: institucion.telefono || '',
      email: institucion.email || '',
      representanteLegal: institucion.representanteLegal || '',
      fechaConstitucion: institucion.fechaConstitucion ? 
        new Date(institucion.fechaConstitucion).toISOString().split('T')[0] : '',
      estatus: institucion.estatus,
      documentosRequeridos: institucion.documentosRequeridos || []
    });
    setShowModal(true);
  };

  // Resetear formulario
  const resetFormData = () => {
    setFormData({
      nombre: '',
      rfc: '',
      direccion: '',
      telefono: '',
      email: '',
      representanteLegal: '',
      fechaConstitucion: '',
      estatus: 'pendiente',
      documentosRequeridos: []
    });
    setInstitucionSeleccionada(null);
    setNuevoDocumentoReq('');
  };

  // Agregar documento requerido
  const handleAgregarDocumentoReq = () => {
    if (nuevoDocumentoReq.trim()) {
      setFormData({
        ...formData,
        documentosRequeridos: [...formData.documentosRequeridos, nuevoDocumentoReq.trim()]
      });
      setNuevoDocumentoReq('');
    }
  };

  // Quitar documento requerido
  const handleQuitarDocumentoReq = (index) => {
    const nuevos = [...formData.documentosRequeridos];
    nuevos.splice(index, 1);
    setFormData({ ...formData, documentosRequeridos: nuevos });
  };

  // Obtener badge de estatus
  const getEstatusBadge = (estatus) => {
    const option = estatusOptions.find(o => o.value === estatus);
    return (
      <span className={`estatus-badge ${estatus}`} style={{ 
        backgroundColor: option?.color + '20', 
        color: option?.color 
      }}>
        {option?.label || estatus}
      </span>
    );
  };

  return (
    <div className="instituciones-admin">
      <div className="header">
        <div>
          <h1>Instituciones</h1>
          <p>Gestiona las instituciones de asistencia privada</p>
        </div>
        <button className="btn-primary" onClick={() => {
          resetFormData();
          setShowModal(true);
        }}>
          + Nueva Institución
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-card">
        <div className="filtros-grid">
          <div className="filtro-group">
            <label>Buscar</label>
            <input
              type="text"
              placeholder="Nombre, RFC o email..."
              value={filtros.busqueda}
              onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
              className="form-control"
            />
          </div>

          <div className="filtro-group">
            <label>Estatus</label>
            <select
              value={filtros.estatus}
              onChange={(e) => setFiltros({...filtros, estatus: e.target.value})}
              className="form-control"
            >
              {estatusOptions.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label>Fecha desde</label>
            <input
              type="date"
              value={filtros.fechaDesde}
              onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
              className="form-control"
            />
          </div>

          <div className="filtro-group">
            <label>Fecha hasta</label>
            <input
              type="date"
              value={filtros.fechaHasta}
              onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
              className="form-control"
            />
          </div>

          <div className="filtro-actions">
            <button 
              className="btn-secondary"
              onClick={() => setFiltros({
                estatus: 'todos',
                busqueda: '',
                fechaDesde: '',
                fechaHasta: ''
              })}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Grid de instituciones */}
      <div className="instituciones-grid">
        {loading ? (
          <div className="text-center">Cargando instituciones...</div>
        ) : instituciones.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🏢</span>
            <p>No hay instituciones para mostrar</p>
          </div>
        ) : (
          instituciones.map(inst => (
            <div key={inst._id} className="institucion-card" onClick={() => cargarDetalleInstitucion(inst._id)}>
              <div className="card-header">
                <div className="institucion-icon">🏢</div>
                <div className="institucion-titulo">
                  <h3>{inst.nombre}</h3>
                  <p className="institucion-rfc">RFC: {inst.rfc}</p>
                </div>
                {getEstatusBadge(inst.estatus)}
              </div>

              <div className="card-body">
                <p className="institucion-contacto">
                  <span>📧 {inst.email || 'Sin email'}</span>
                  <span>📞 {inst.telefono || 'Sin teléfono'}</span>
                </p>
                <p className="institucion-direccion">
                  📍 {inst.direccion || 'Dirección no especificada'}
                </p>
              </div>

              <div className="card-footer">
                <div className="stats-mini">
                  <div className="stat-mini" title="Usuarios">
                    <span>👥</span>
                    <span>{inst.stats?.usuarios || 0}</span>
                  </div>
                  <div className="stat-mini" title="Documentos">
                    <span>📄</span>
                    <span>{inst.stats?.documentos || 0}</span>
                  </div>
                  <div className="stat-mini" title="Pendientes">
                    <span>⏳</span>
                    <span>{inst.stats?.documentosPendientes || 0}</span>
                  </div>
                </div>
                <button className="btn-ver-mas">Ver detalles →</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal para crear/editar institución */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <h2>{institucionSeleccionada?._id ? 'Editar Institución' : 'Nueva Institución'}</h2>
            
            <form onSubmit={handleGuardarInstitucion}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>RFC *</label>
                  <input
                    type="text"
                    value={formData.rfc}
                    onChange={(e) => setFormData({...formData, rfc: e.target.value})}
                    className="form-control"
                    required
                    maxLength="13"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Representante Legal</label>
                  <input
                    type="text"
                    value={formData.representanteLegal}
                    onChange={(e) => setFormData({...formData, representanteLegal: e.target.value})}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Constitución</label>
                  <input
                    type="date"
                    value={formData.fechaConstitucion}
                    onChange={(e) => setFormData({...formData, fechaConstitucion: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>

              {institucionSeleccionada?._id && (
                <div className="form-group">
                  <label>Estatus</label>
                  <select
                    value={formData.estatus}
                    onChange={(e) => setFormData({...formData, estatus: e.target.value})}
                    className="form-control"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="pendiente">Pendiente</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Documentos Requeridos</label>
                <div className="documentos-req-input">
                  <input
                    type="text"
                    value={nuevoDocumentoReq}
                    onChange={(e) => setNuevoDocumentoReq(e.target.value)}
                    className="form-control"
                    placeholder="Ej: Acta constitutiva"
                  />
                  <button type="button" className="btn-add" onClick={handleAgregarDocumentoReq}>
                    Agregar
                  </button>
                </div>
                
                <div className="documentos-req-lista">
                  {formData.documentosRequeridos.map((doc, index) => (
                    <span key={index} className="documento-req-tag">
                      {doc}
                      <button type="button" onClick={() => handleQuitarDocumentoReq(index)}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowModal(false);
                  resetFormData();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {institucionSeleccionada?._id ? 'Actualizar' : 'Crear'} Institución
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de detalle de institución */}
      {showDetalleModal && institucionSeleccionada && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <div className="detalle-header">
              <h2>{institucionSeleccionada.nombre}</h2>
              <button className="close-btn" onClick={() => setShowDetalleModal(false)}>×</button>
            </div>

            {/* Pestañas */}
            <div className="detalle-tabs">
              <div className="tab-buttons">
                <button 
                  className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  Información
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'documentos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documentos')}
                >
                  Documentos ({documentosInstitucion?.length || 0})
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'usuarios' ? 'active' : ''}`}
                  onClick={() => setActiveTab('usuarios')}
                >
                  Usuarios ({usuariosInstitucion?.length || 0})
                </button>
              </div>

              <div className="tab-content">
                {activeTab === 'info' && (
                  <div className="info-grid">
                    <div className="info-item">
                      <label>RFC</label>
                      <p>{institucionSeleccionada.rfc}</p>
                    </div>
                    <div className="info-item">
                      <label>Estatus</label>
                      <p>{getEstatusBadge(institucionSeleccionada.estatus)}</p>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <p>{institucionSeleccionada.email || '—'}</p>
                    </div>
                    <div className="info-item">
                      <label>Teléfono</label>
                      <p>{institucionSeleccionada.telefono || '—'}</p>
                    </div>
                    <div className="info-item full-width">
                      <label>Dirección</label>
                      <p>{institucionSeleccionada.direccion || '—'}</p>
                    </div>
                    <div className="info-item">
                      <label>Representante Legal</label>
                      <p>{institucionSeleccionada.representanteLegal || '—'}</p>
                    </div>
                    <div className="info-item">
                      <label>Fecha de Constitución</label>
                      <p>{institucionSeleccionada.fechaConstitucion ? 
                        new Date(institucionSeleccionada.fechaConstitucion).toLocaleDateString() : '—'}</p>
                    </div>
                    <div className="info-item full-width">
                      <label>Documentos Requeridos</label>
                      <div className="documentos-req-lista">
                        {institucionSeleccionada.documentosRequeridos?.length > 0 ? (
                          institucionSeleccionada.documentosRequeridos.map((doc, i) => (
                            <span key={i} className="documento-req-tag">{doc}</span>
                          ))
                        ) : (
                          <p className="text-muted">No hay documentos requeridos</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'documentos' && (
                  <div className="documentos-tab">
                    {!documentosInstitucion || documentosInstitucion.length === 0 ? (
                      <div className="empty-state">
                        <span className="empty-icon">📄</span>
                        <p>No hay documentos para esta institución</p>
                      </div>
                    ) : (
                      <div className="documentos-lista">
                        {documentosInstitucion.map(doc => (
                          <div key={doc._id} className="documento-item">
                            <span className="doc-icon">📄</span>
                            <div className="doc-info">
                              <span className="doc-nombre">{doc.nombre}</span>
                              <span className="doc-fecha">
                                {new Date(doc.fechaSubida).toLocaleDateString()}
                              </span>
                            </div>
                            <span className={`doc-estatus ${doc.estatus}`}>
                              {doc.estatus}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'usuarios' && (
                  <div className="usuarios-tab">
                    {!usuariosInstitucion || usuariosInstitucion.length === 0 ? (
                      <div className="empty-state">
                        <span className="empty-icon">👥</span>
                        <p>No hay usuarios para esta institución</p>
                      </div>
                    ) : (
                      <div className="usuarios-lista">
                        {usuariosInstitucion.map(usuario => (
                          <div key={usuario._id} className="usuario-item">
                            <div className="usuario-avatar">
                              {usuario.nombre?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div className="usuario-info">
                              <span className="usuario-nombre">{usuario.nombre}</span>
                              <span className="usuario-email">{usuario.email}</span>
                            </div>
                            <span className={`estado-badge ${usuario.activo ? 'activo' : 'inactivo'}`}>
                              {usuario.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Estadísticas */}
            <div className="estadisticas-grid">
              <div className="estadistica-card">
                <span className="estadistica-icon">👥</span>
                <div>
                  <h4>{institucionSeleccionada.stats?.usuarios || 0}</h4>
                  <p>Usuarios</p>
                </div>
              </div>
              <div className="estadistica-card">
                <span className="estadistica-icon">📄</span>
                <div>
                  <h4>{institucionSeleccionada.stats?.documentos || 0}</h4>
                  <p>Documentos</p>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="detalle-actions">
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowDetalleModal(false);
                  abrirModalEditar(institucionSeleccionada);
                }}


              >
                Editar Institución
               </button>
                <button 
                className="btn-primary" 
                onClick={() => {
                  setShowDetalleModal(false);
                }}

              >
                Cancelar
              </button>
              {user?.role === 'super_admin_jasp' && (
                <button 
                  className="btn-danger"
                  onClick={() => handleEliminarInstitucion(institucionSeleccionada._id)}
                >
                  Eliminar Institución
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Instituciones;